import type { StatusTone } from "@/lib/status-colors"
import { t } from "@/lib/i18n"

/**
 * Shape, endpoints and copy lookup for the CFDI attached to a bill.
 *
 * **The browser decides nothing about an invoice.** Whether a bill may be
 * invoiced, whether a document may be cancelled and what the failure said are
 * all answered on the server — by `Location::InvoiceCreationService`,
 * `Location::InvoiceCancelService` and `Cfdi::ErrorCatalog` — and rendered here
 * verbatim. The same rule the refund feature states, for a sharper reason: a
 * screen that decided for itself would offer to spend a paid stamp on a
 * document the SAT is going to refuse.
 *
 * ## `pending` is not "nothing happened yet"
 *
 * The row exists before the stamp does. A manager who clicks "Facturar" while
 * the SAT is down gets an invoice immediately and a folio later, and the drawer
 * has to say which of the two it is looking at. That is why `uuid` is nullable
 * and why the panel never calls a `pending` invoice issued.
 */

export type InvoiceStatus =
  | "pending"
  | "stamped"
  | "failed"
  | "cancelled"
  | "cancellation_pending"

/** `bills[].invoice` — the CFDI currently claiming the bill, or null. */
export type BillInvoice = {
  id: number
  status: string
  statusLabel: string
  kind: string
  kindLabel: string
  /** The SAT's folio. `null` until it stamps, which is what makes it real. */
  uuid: string | null
  series: string | null
  folio: string | null
  stampedAt: string | null
  cancelledAt: string | null
  cancellationReasonCode: string | null
  cancellationReasonLabel: string | null
  usageCode: string
  usageLabel: string
  receiverRfc: string
  receiverLegalName: string
  totalCents: number
  currency: string
  /** The readable sentence. Not the SAT code — see Staff::InvoiceSerializer. */
  lastError: string | null
  attempts: number
  /** Spent its attempts, or classified permanent. Either way a person's move. */
  exhausted: boolean
  cancellable: boolean
}

export type CatalogOption = { value: string; label: string }

/**
 * The static props the bills screen receives, or `null` when the restaurant does
 * not have `cfdi_invoicing` switched on. `null` means the panel is not rendered
 * at all, matching the manage screens' rule about controls for features that are
 * off.
 */
export type CfdiSettings = {
  /** A `Location::TaxProfile` with a PAC credential exists. */
  configured: boolean
  /** This staff member is a manager. The route enforces it too. */
  canIssue: boolean
  usageCodes: CatalogOption[]
  defaultUsageCode: string
  taxRegimeCodes: CatalogOption[]
  cancellationReasons: CatalogOption[]
  /** Motive `01`, the one that requires the folio of the replacing document. */
  substitutionReasonCode: string
}

export const INVOICE_PATHS = {
  create: (billId: number) => `/bills/${billId}/invoices`,
  cancel: (invoiceId: number) => `/invoices/${invoiceId}/cancel`,
} as const

/** The location feature that has to be on for either endpoint to answer. */
export const CFDI_FEATURE = "cfdi_invoicing"

/** Where a manager goes to fix "the restaurant has no fiscal details". */
export const TAX_PROFILE_PATH = "/manage/tax_profile"

const I18N_PREFIX = "ui.react.features.staff.invoices"

export function tInvoice(key: string, options: Record<string, string | number> = {}) {
  return t(`${I18N_PREFIX}.${key}`, options)
}

/**
 * The tone of each lifecycle state.
 *
 * `cancellation_pending` is `warning` rather than `disabled` on purpose: the
 * document is still live, the receiver has not answered, and treating it as
 * finished is precisely the lie that state exists to prevent.
 *
 * An unrecognised status falls back to `disabled` rather than to `success`: a
 * state this build cannot name must not be drawn as if everything is fine.
 */
const STATUS_TONES: Record<string, StatusTone> = {
  pending: "reserved",
  stamped: "success",
  failed: "danger",
  cancelled: "disabled",
  cancellation_pending: "warning",
}

export function invoiceTone(status: string): StatusTone {
  return STATUS_TONES[status] ?? "disabled"
}

/**
 * Cents to major units for `formatMoney`.
 *
 * A unit conversion for display, not a derived total: `Cfdi::DocumentBuilder`
 * decided `totalCents` and this screen shows that number and no other. Integer
 * cents is what `location_invoices` stores precisely because a CFDI total has to
 * round-trip to the PAC unchanged.
 */
export function invoiceAmount(cents: number): number {
  return Number.isFinite(cents) ? cents / 100 : 0
}
